import { useEffect, useRef, useState } from "react";

// `once` (default true) disconnects after the first time the element enters
// view — the original scroll-reveal behavior, where "has it ever been seen"
// is all that matters. Pass `once: false` to keep tracking visibility as the
// element scrolls in and out (e.g. driving an ambient effect only while
// something is actually on screen).
export default function useInView<T extends HTMLElement>(
  threshold = 0.3,
  options?: { once?: boolean }
) {
  const once = options?.once ?? true;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting && once) {
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once]);

  return { ref, inView };
}
